import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Volume2, 
  Brain, 
  Database, 
  GitBranch,
  Plus,
  Search,
  Folder
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useDnD } from '../stores/DnDcontext';
import { useAuthStore } from '../stores/authStore';
import { fetchNodeConfigsWithFallback } from '../utils/nodeConfigApi';

const NODE_CONFIG_CACHE_KEY = 'ai-studio-node-config-cache-v1';

const Sidebar = ({ onNodeAdd }) => {
  const [nodeConfigs, setNodeConfigs] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dragNodeData, setDragNodeData] = useDnD();

  // ノード設定を取得
  useEffect(() => {
    const fetchNodeConfigs = async () => {
      let hasCachedConfigs = false;

      const cachedRaw = localStorage.getItem(NODE_CONFIG_CACHE_KEY);
      if (cachedRaw) {
        try {
          const cached = JSON.parse(cachedRaw);
          if (cached && typeof cached === 'object' && Object.keys(cached).length > 0) {
            setNodeConfigs(cached);
            hasCachedConfigs = true;
          }
        } catch (_error) {
          // ignore broken cache
        }
      }

      try {
        const { configs: normalizedConfigs } = await fetchNodeConfigsWithFallback();

        setNodeConfigs(normalizedConfigs);
        localStorage.setItem(NODE_CONFIG_CACHE_KEY, JSON.stringify(normalizedConfigs));
      } catch (error) {
        console.error('ノード設定取得エラー:', error);
        useAuthStore.getState().checkAuth();
        if (!hasCachedConfigs) {
          toast.error('ノード設定の取得に失敗しました（ログイン状態またはAPI接続をご確認ください）');
        } else {
          toast.error('ノード設定の更新に失敗したため、キャッシュを表示しています');
        }
      }
    };
    
    fetchNodeConfigs();
  }, []);

  // ノードアイコンマップ
  const nodeIcons = {
    text_processor: FileText,
    audio_converter: Volume2,
    whisper_transcribe: Volume2,
    gpt_processor: Brain,
    file_uploader: Database,
    condition_checker: GitBranch,
  };

  // カテゴリー別色
  const categoryColors = {
    input: 'bg-blue-100 text-blue-800 border-blue-200',
    output: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    text: 'bg-green-100 text-green-800 border-green-200',
    audio: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    ai: 'bg-purple-100 text-purple-800 border-purple-200',
    storage: 'bg-red-100 text-red-800 border-red-200',
    logic: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  const categoryMeta = {
    input: { label: '入力', icon: FileText },
    output: { label: '出力', icon: FileText },
    text: { label: 'テキスト', icon: FileText },
    audio: { label: '音声', icon: Volume2 },
    ai: { label: 'AI', icon: Brain },
    storage: { label: 'ストレージ', icon: Database },
    logic: { label: 'ロジック', icon: GitBranch },
  };

  const discoveredCategories = Array.from(new Set(
    Object.values(nodeConfigs)
      .map((config) => config?.category)
      .filter(Boolean)
  ));

  const orderedCategories = [
    ...Object.keys(categoryMeta).filter((key) => discoveredCategories.includes(key)),
    ...discoveredCategories.filter((key) => !Object.keys(categoryMeta).includes(key)),
  ];

  const categories = [
    { key: 'all', label: 'すべて', icon: Folder },
    ...orderedCategories.map((key) => ({
      key,
      label: categoryMeta[key]?.label || key,
      icon: categoryMeta[key]?.icon || Folder,
    }))
  ];

  // ノードフィルタリング
  const filteredNodes = Object.entries(nodeConfigs).filter(([nodeType, config]) => {
    // カテゴリーフィルター
    if (selectedCategory !== 'all' && config.category !== selectedCategory) {
      return false;
    }
    
    // 検索フィルター
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        config.name.toLowerCase().includes(searchLower) ||
        config.description.toLowerCase().includes(searchLower) ||
        nodeType.toLowerCase().includes(searchLower)
      );
    }
    
    return true;
  });

  // ドラッグ開始
  const onDragStart = (event, nodeType, config) => {
    const nodeData = {
      nodeType,
      config
    };
    setDragNodeData(nodeData);

    event.dataTransfer.effectAllowed = 'move';
  };

  // ドラッグ終了
  const onDragEnd = () => {
    setDragNodeData(null);
  };

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col min-h-0">
      {/* ヘッダー */}
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          ノードライブラリ
        </h2>
        
        {/* 検索バー */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="ノードを検索..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-studio-500 focus:border-transparent"
          />
        </div>
        
        {/* カテゴリータブ */}
        <div className="flex flex-wrap gap-1">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.key}
                onClick={() => setSelectedCategory(category.key)}
                className={clsx(
                  'flex items-center space-x-1 px-3 py-1.5 text-xs rounded-full transition-colors',
                  selectedCategory === category.key
                    ? 'bg-studio-100 text-studio-800 border border-studio-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                <Icon size={12} />
                <span>{category.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ノード一覧 */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2 custom-scrollbar">
        {filteredNodes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Search size={32} className="mx-auto mb-2 opacity-50" />
            <p>該当するノードが見つかりません</p>
          </div>
        ) : (
          filteredNodes.map(([nodeType, config]) => {
            const Icon = nodeIcons[nodeType] || FileText;
            const categoryColor = categoryColors[config.category] || categoryColors.text;
            const isDragging = dragNodeData?.nodeType === nodeType;
            
            return (
              <div
                key={nodeType}
                draggable
                onDragStart={(e) => onDragStart(e, nodeType, config)}
                onDragEnd={onDragEnd}
                onClick={() => onNodeAdd(nodeType, config)}
                className={clsx(
                  'p-3 border rounded-lg cursor-move transition-all duration-200 hover:shadow-md',
                  isDragging ? 'opacity-50 scale-95' : 'opacity-100 scale-100',
                  'bg-white border-gray-200 hover:border-studio-300'
                )}
              >
                <div className="flex items-start space-x-3">
                  {/* アイコン */}
                  <div className={clsx(
                    'p-2 rounded-lg border flex-shrink-0',
                    categoryColor
                  )}>
                    <Icon size={16} />
                  </div>
                  
                  {/* 情報 */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-800 text-sm mb-1 truncate">
                      {config.name}
                    </h3>
                    <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                      {config.description}
                    </p>
                    
                    {/* 入出力情報 */}
                    <div className="flex items-center space-x-3 text-xs text-gray-500">
                      <span>入力: {config.inputs?.length || 0}</span>
                      <span>出力: {config.outputs?.length || 0}</span>
                    </div>
                  </div>
                </div>
                
                {/* カテゴリーバッジ */}
                <div className="mt-2 flex justify-between items-center">
                  <span className={clsx(
                    'px-2 py-1 text-xs rounded-full border',
                    categoryColor
                  )}>
                    {config.category}
                  </span>
                  
                  <Plus size={14} className="text-gray-400" />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* フッター */}
      <div className="p-4 border-t border-gray-200">
        <div className="text-xs text-gray-500 text-center">
          ノードをドラッグまたはクリックで追加
        </div>
      </div>
    </div>
  );
};

export default Sidebar;